import React from 'react';

import { Button, Modal, MultiSelect, Space, TextInput, Timeline } from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconNumber1, IconNumber2, IconPlus } from '@tabler/icons-react';
import { useMutation } from '@tanstack/react-query';
import { MultiSelectItem } from 'components/MultiSelect/MultiSelectItem';
import { MultiSelectValue } from 'components/MultiSelect/MultiSelectValue';
import { useTranslation } from 'react-i18next';
import { NotificationService } from 'services/notification-service';
import { ResourceService } from 'services/resource-service';
import { SmartAssistantService } from 'services/smart-assistant-service';
import { Resource } from 'shared/interfaces/Resource';
import { SmartAssistant } from 'shared/interfaces/SmartAssistant';
import { maxLengthValidator, nonEmptyArrayValidator, requiredInputValidator } from 'shared/utils/form-validators';
import { getResourcesAsSelectItems } from 'shared/utils/resource';
import { getSmartAssistantsAsSelectItems } from 'shared/utils/smart-assistant';

interface TagCreateModalProps {
  opened: boolean;
  onClose: () => void;
  resources?: Resource[];
  smartAssistants?: SmartAssistant[];
  onTagCreated?: () => void;
}

export function TagCreateModal({ opened, onClose, resources, smartAssistants, onTagCreated }: TagCreateModalProps) {
  const { t } = useTranslation();

  const getSelectData = () => {
    if (resources) return getResourcesAsSelectItems(resources);
    if (smartAssistants) return getSmartAssistantsAsSelectItems(smartAssistants);
    return [];
  };

  const tagCreationForm = useForm({
    initialValues: { tag: '', items: [] as string[] },
    validate: {
      tag: (value) => requiredInputValidator(value) || maxLengthValidator(value),
      items: (value) => nonEmptyArrayValidator(value)
    }
  });

  const smartAssistantTagCreationMutation = useMutation({
    mutationFn: () => SmartAssistantService.createTag(tagCreationForm.values.tag, tagCreationForm.values.items),
    onSuccess: () => NotificationService.showResult(true, t('FEED_BACK.TAG.CREATE.SUCCESS')),
    onError: () => NotificationService.showResult(false, t('FEED_BACK.TAG.CREATE.FAILURE')),
    onSettled: () => {
      if (onTagCreated) onTagCreated();
      tagCreationForm.reset();
      close();
    }
  });

  const resourceTagCreationMutation = useMutation({
    mutationFn: () => ResourceService.createTag(tagCreationForm.values.tag, tagCreationForm.values.items),
    onSuccess: () => NotificationService.showResult(true, t('FEED_BACK.TAG.CREATE.SUCCESS')),
    onError: () => NotificationService.showResult(false, t('FEED_BACK.TAG.CREATE.FAILURE')),
    onSettled: () => {
      if (onTagCreated) onTagCreated();
      close();
    }
  });

  const onCreateClick = () => {
    const tagCreationFormValidation = tagCreationForm.validate();
    if (tagCreationFormValidation.hasErrors) return;
    if (resources) resourceTagCreationMutation.mutate();
    if (smartAssistants) smartAssistantTagCreationMutation.mutate();
  };

  const close = () => {
    tagCreationForm.reset();
    onClose();
  };

  return (
    <Modal padding="md" opened={opened} withCloseButton onClose={close} title={t('TAG.CREATE.TITLE')}>
      <Timeline
        bulletSize={20}
        lineWidth={3}
        active={2}
        styles={{
          itemTitle: {
            padding: 0
          },
          itemContent: {
            margin: 0
          }
        }}>
        <Timeline.Item
          bullet={<IconNumber1 style={{ width: 15, height: 15 }} />}
          title={
            <TextInput placeholder={t('TAG.CREATE.PLACE_HORDER_NAME')} {...tagCreationForm.getInputProps('tag')} />
          }
        />

        <Timeline.Item
          bullet={<IconNumber2 style={{ width: 15, height: 15 }} />}
          title={
            <MultiSelect
              searchable
              limit={20}
              withinPortal
              data={getSelectData()}
              valueComponent={MultiSelectValue}
              itemComponent={MultiSelectItem}
              placeholder={t('TAG.CREATE.PLACE_HOLDER_ITEMS')}
              {...tagCreationForm.getInputProps('items')}
            />
          }></Timeline.Item>
      </Timeline>
      <Space h="xs" />
      <Button
        my="xs"
        style={{ float: 'right' }}
        loading={resourceTagCreationMutation.isLoading || smartAssistantTagCreationMutation.isLoading}
        leftIcon={<IconPlus />}
        onClick={onCreateClick}>
        {t('APP.ACTIONS.CREATE')}
      </Button>
    </Modal>
  );
}
